import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { hasMailPassword } from '../utils/mailCredentials';
import { notSpamRequest, runMailMoves, moveErrorMessage } from '../utils/reportSpam';
import { moveMailRequest } from '../utils/moveMail';
import MoveToFolderButton from './MoveToFolderButton';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import MailListRow from './MailListRow';
import MailReadingPane from './MailReadingPane';
import './Inbox.css';
import './FolderView.css';

const isImapMail = (mail) => mail && (mail.source === 'imap' || mail.uid);
const getMailUid = (mail) => mail?.uid ?? mail?.imapUid;
const getMailKey = (mail) => String(getMailUid(mail) ?? mail?._id ?? '');

/**
 * Spam Component
 * Displays all spam mails for the logged-in user
 */
const Spam = ({ currentUser }) => {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchSpam();
      
      // Auto-refresh spam mails every 30 seconds (without showing loading spinner)
      const interval = setInterval(() => {
        fetchSpam(false);
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchSpam = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    try {
      const [mailbox, stored] = await Promise.allSettled([
        hasMailPassword() ? axiosInstance.get('/api/mail/server/spam') : Promise.resolve(null),
        axiosInstance.get('/api/mail/spam'),
      ]);
      const rowsOf = (outcome) =>
        outcome.status === 'fulfilled' && outcome.value?.data?.success
          ? outcome.value.data.data || []
          : [];
      const merged = [...rowsOf(mailbox), ...rowsOf(stored)].sort(
        (a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0)
      );
      setMails(merged);
      const failed = [mailbox, stored].filter((outcome) => outcome.status === 'rejected');
      if (!merged.length && failed.length) {
        setError(failed[0].reason?.response?.data?.message || 'Error fetching spam mails. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching spam mails:', error);
      setError(error.response?.data?.message || 'Error fetching spam mails. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleMailClick = async (mail) => {
    const mailKey = getMailKey(mail);
    setSelectedMail(mail);

    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      try {
        const detail = await axiosInstance.get(`/api/mail/server/message/${uid}`, {
          params: { folder: 'spam' },
        });
        if (detail.data.success) setSelectedMail({ ...detail.data.data, folder: 'spam' });
      } catch (error) {
        console.error('Error loading spam message:', error);
      }
      return;
    }

    if (!mail.isRead && mail._id) {
      try {
        await axiosInstance.patch(`/api/mail/mark-read/${mail._id}`);
        setMails((prev) => prev.map((m) => (getMailKey(m) === mailKey ? { ...m, isRead: true } : m)));
        setSelectedMail({ ...mail, isRead: true });
      } catch (error) {
        console.error('Error marking mail as read:', error);
      }
    }
  };

  const handleCloseMail = () => {
    setSelectedMail(null);
  };

  // Selection handlers
  const handleSelectMail = (mailId) => {
    setSelectedMailIds(prev => 
      prev.includes(mailId) 
        ? prev.filter(id => id !== mailId)
        : [...prev, mailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMailIds.length === mails.length) {
      setSelectedMailIds([]);
    } else {
      setSelectedMailIds(mails.map(getMailKey));
    }
  };

  const handleDeselectAll = () => {
    setSelectedMailIds([]);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMailIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      const selectedMails = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
      const deletePromises = selectedMails.map((mail) => {
        if (isImapMail(mail)) {
          const uid = getMailUid(mail);
          return axiosInstance.patch(`/api/mail/trash/${uid}`, { folder: 'spam' });
        }
        return axiosInstance.patch(`/api/mail/move-to-trash/${mail._id}`);
      });
      await Promise.all(deletePromises);

      const removed = new Set(selectedMails.map(getMailKey));
      setMails(mails.filter((m) => !removed.has(getMailKey(m))));
      if (selectedMail && removed.has(getMailKey(selectedMail))) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      fetchSpam();
    } catch (error) {
      console.error('Error deleting mails:', error);
      setAlertMessage('Error deleting mails. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkMarkUnread = async () => {
    if (selectedMailIds.length === 0) return;

    try {
      setMails(mails.map(m => 
        selectedMailIds.includes(getMailKey(m)) ? { ...m, isRead: false } : m
      ));
      setSelectedMailIds([]);
    } catch (error) {
      console.error('Error marking mails as unread:', error);
    }
  };

  const handleNotSpam = async () => {
    const targets = selectedMailIds.length
      ? mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)))
      : selectedMail
        ? [selectedMail]
        : [];
    if (targets.length === 0) return;

    try {
      const { moved, failed } = await runMailMoves(targets, notSpamRequest);
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not move mail out of spam. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error moving mail out of spam:', error);
      setAlertMessage('Could not move mail out of spam. Please try again.');
      setShowAlert(true);
    }
  };

  const handleMoveToFolder = async (destination) => {
    const targets = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    if (!targets.length) return;

    try {
      const { moved, failed } = await runMailMoves(
        targets,
        (mail) => moveMailRequest(mail, destination, 'spam')
      );
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not move mail. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error moving mail:', error);
      setAlertMessage('Could not move mail. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkDownload = () => {
    if (selectedMailIds.length === 0) return;
    const selectedMails = mails.filter(m => selectedMailIds.includes(getMailKey(m)));
    selectedMails.forEach(mail => {
      const mailContent = `From: ${mail.from}\nTo: ${mail.to}\nSubject: ${mail.subject}\nDate: ${formatFullDate(mail.createdAt)}\n\n${mail.body}`;
      const blob = new Blob([mailContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mail.subject || 'mail'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading spam mails...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchSpam} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`inbox-container premium-mail-dashboard ${selectedMail ? 'mail-detail-open' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Spam</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">
            {mails.length} {mails.length === 1 ? 'spam mail' : 'spam mails'}
          </p>
        </div>
      </div>

      <div className={`inbox-content reading-pane-no-split gmail-split-layout${selectedMail ? ' has-detail-open full-detail-view' : ''}`}>
        <div className={`mail-list-container ${selectedMail ? 'hidden' : 'full-width'}`}>
          {mails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button 
                  className="toolbar-btn checkbox-btn"
                  onClick={selectedMailIds.length > 0 ? handleDeselectAll : handleSelectAll}
                  title={selectedMailIds.length > 0 ? "Deselect all" : "Select all"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    {selectedMailIds.length > 0 && (
                      <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                    )}
                  </svg>
                  {selectedMailIds.length > 0 && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={fetchSpam}
                  title="Refresh"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                
                {selectedMailIds.length > 0 && (
                  <>
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDownload}
                      title="Download"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleNotSpam}
                      title="Not spam"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDelete}
                      title="Delete"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              
              {selectedMailIds.length > 0 && (
                <>
                  <div className="toolbar-separator"></div>
                  <div className="toolbar-right">
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkUnread}
                      title="Mark as unread"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <circle cx="18" cy="4" r="3" fill="currentColor" className="notification-dot"/>
                    </button>
                    
                    <MoveToFolderButton currentFolder="spam" onMove={handleMoveToFolder} />
                    
                    <button 
                      className="toolbar-btn"
                      title="More options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="mail-list">
            {mails.length === 0 ? (
              <div className="empty-inbox">
                <p>No spam mails</p>
                <p className="empty-hint">Spam mails will appear here</p>
              </div>
            ) : (
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                return (
                <MailListRow
                  key={mailKey}
                  mail={mail}
                  mailKey={mailKey}
                  currentUserEmail={currentUser?.email}
                  isChecked={selectedMailIds.includes(mailKey)}
                  isOpen={selectedMail && getMailKey(selectedMail) === mailKey}
                  isUnread={!mail.isRead && !mail.seen}
                  onOpen={handleMailClick}
                  onToggleCheck={handleSelectMail}
                />
                );
              })
            )}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel="Spam"
            onClose={handleCloseMail}
            headerActions={[
              {
                key: 'not-spam',
                title: 'Not spam',
                onClick: () => handleNotSpam(),
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                ),
              },
            ]}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${selectedMailIds.length} mail(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};

export default Spam;


